import React, { useState } from 'react';
import {
  getNotifyEnabled,
  setNotifyEnabled,
  getNotifyEmail,
  setNotifyEmail,
  validateNotifySettings,
} from '../../../utils/notifySettings';

/**
 * issue #98 FTask3: 完了メール通知トグル + 宛先メアド入力。default OFF (opt-in)。
 * AutoSaveToggle と並べて配置し、 同じトーン (accent-amber-500 / text-slate-700) で統一する。
 * 通知 ON のときだけメアド入力欄を表示する (段階的開示)。
 *
 * issue #111 Task C: disabled=true (自動保存 OFF 時) で checkbox を無効化 + 淡色 + ヒント表示。
 * 内部の notify-enabled 設定 (localStorage) は変更しない (視覚のみ)。
 */
export interface NotifyToggleProps {
  disabled?: boolean;
}

export const NotifyToggle: React.FC<NotifyToggleProps> = ({ disabled = false }) => {
  const [enabled, setEnabled] = useState<boolean>(() => getNotifyEnabled());
  const [email, setEmail] = useState<string>(() => getNotifyEmail());

  const handleToggle = (): void => {
    const next = !enabled;
    setNotifyEnabled(next);
    setEnabled(next);
  };

  const handleEmail = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
    setNotifyEmail(e.target.value);
  };

  // 通知 ON かつ自動保存 ON (= !disabled) のときだけメアド欄を出す (段階的開示)。
  const showEmailField = enabled && !disabled;
  // issue #137: メアド欄が出ているときだけ空欄バリデーションを行う (欄が無ければ検証しない)。
  const emailError = showEmailField ? validateNotifySettings({ enabled, email }) : null;

  return (
    <div className="flex flex-col gap-2">
      <label
        className={`flex items-center gap-2 text-sm select-none ${
          disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-700'
        }`}
      >
        <input
          type="checkbox"
          checked={enabled}
          onChange={handleToggle}
          disabled={disabled}
          className={`w-4 h-4 accent-amber-500 ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
          aria-label="完了をメールで通知"
        />
        <span>完了をメールで通知</span>
      </label>
      {disabled && (
        <span className="text-xs text-slate-400 ml-6">自動保存 ON で有効</span>
      )}
      {showEmailField && (
        <>
          <input
            type="email"
            value={email}
            onChange={handleEmail}
            placeholder="通知先メールアドレス"
            aria-label="通知先メールアドレス"
            aria-invalid={emailError !== null}
            className={`text-sm border rounded px-2 py-1 ml-6 w-full max-w-xs focus:outline-none focus:ring-2 ${
              emailError
                ? 'border-red-400 focus:ring-red-400'
                : 'border-slate-300 focus:ring-amber-400'
            }`}
          />
          {emailError && (
            <span role="alert" className="text-xs text-red-600 ml-6">
              {emailError}
            </span>
          )}
          <span className="text-xs text-slate-500 ml-6">
            ⚠️ 迷惑メールフォルダに入る場合があります
          </span>
        </>
      )}
    </div>
  );
};
